/**
 * Vault API service for frontend
 */

import { API_BASE_URL } from '../config/settings';

export interface VaultValue {
  key: string;
  value: string;
  history: Array<{
    value: string;
    date: number;
  }>;
}

export interface VaultItem {
  id: string;
  name: string;
  values: VaultValue[];
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

export interface VaultItemRequest {
  name: string;
  values: Array<{
    key: string;
    value: string;
  }>;
  notes?: string;
}

export interface VaultListResponse {
  items: VaultItem[];
  total: number;
}

class VaultApi {
  private baseUrl = `${API_BASE_URL}/vault`;

  async list(search?: string): Promise<VaultListResponse> {
    const params = new URLSearchParams();
    if (search) params.set('search', search);

    const res = await fetch(`${this.baseUrl}?${params.toString()}`);
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch vault items');
    }

    return data.data;
  }

  async get(id: string): Promise<VaultItem> {
    const res = await fetch(`${this.baseUrl}/${id}`);
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch vault item');
    }

    return data.data;
  }

  async create(item: VaultItemRequest): Promise<VaultItem> {
    const res = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    });
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to create vault item');
    }

    return data.data;
  }

  async update(id: string, item: Partial<VaultItemRequest>): Promise<VaultItem> {
    const res = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    });
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to update vault item');
    }

    return data.data;
  }

  async delete(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE'
    });
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete vault item');
    }
  }

  async seed(): Promise<{ seeded: boolean; count?: number; message?: string }> {
    const res = await fetch(`${this.baseUrl}/seed`, {
      method: 'POST'
    });
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to seed vault');
    }

    return data.data;
  }
}

export const vaultApi = new VaultApi();
